<template>
    <div>
        <!-- Card stats -->
        <transition name="alert-fade" mode="out-in">
            <b-alert :variant="variant" :show="dismissCountDown" dismissible @dismiss-count-down="countDownChanged" data-test="save-alert">
                {{ message }}
            </b-alert>
        </transition>
        
        <b-row>
            <!-- Inputs -->
            <b-col xl="4">
                <b-card>
                    <HaloInputs :fields="getScenario.fields" :scenarioName="scenarioName"/>
                    <!-- A checkbox that model's the value of the scenario's beta mode setting -->
                    <b-col v-if="userIsInBetaGroup(scenarioName)">
                        <center>
                            <span class="label mr-5">Run Beta Rules:</span>
                            <b-form-checkbox v-model="getScenarioByName(scenarioName).runBetaRules" name="check-button" class="ml--2 mr-4 pt-2" inline switch></b-form-checkbox>
                        </center>
                    </b-col>

                    <!-- the run and save scenario buttons -->
                    <b-col>
                        <center>
                        <b-button-group class="b-group">
                            <b-button 
                                class="my-3 run-button" 
                                variant="info" 
                                @click="runScenario($event);"
                            >Run Scenario</b-button>
                            <b-button
                                :class="`
                                  my-3
                                  ${!getHasRun || getSaved ? 'ui-state-disabled' : ''}
                                  save-button
                                `"
                                variant="success"
                                :disabled="!getHasRun || getSaved"
                                @click="saveScenario($event)"
                            >Save Scenario</b-button
                            >
                        </b-button-group>
                        </center>
                    </b-col>
                </b-card>
            </b-col>
            <!-- End Inputs -->

            <!--Tables-->
            <b-col xl="8" class="mb-5 mb-xl-0">
                <rules-table :compare="false" :scenarioName="scenarioName"></rules-table>
            </b-col>
            <!--End tables-->
        </b-row>

        <!-- Overlay -->
        <v-overlay z-index="2000" :value="loading">
            <v-progress-circular indeterminate size="72" width="6" color="#40C4FF"></v-progress-circular>
        </v-overlay>
        <!-- End Overlay -->
    </div>
</template>
<script>
// Tables
import RulesTable from "../components/Results/RulesTable";
import HaloInputs from "@/components/Inputs/HaloInputs/HaloInputs";
import useVuelidate from "@vuelidate/core";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { ruleDialog, alertMixins, dialogMixins } from "@/mixins/dialog.js";

export default {
    components: {
        RulesTable,
        HaloInputs,
    },
    setup() {
        return {
            v$: useVuelidate(),
        };
    },
    data() {
        return {
            loading: false,
            // show: false,
            scenarioName: "haloScenario",
        };
    },
    methods: {
        ...mapMutations([
            "setSelectedLoanId",
            "setInvalidSections",
            "removeRuleOverrides",
            "setUsers"
        ]),
        ...mapActions([
            "fetchScenario", 
            "fetchScenarioTimes", 
            "storeRunScenario", 
            "storeSaveScenario", 
            "checkChangeInExceptions",
            "fetchNewEncompassValues"
        ]),

        async runScenario(ev) {
            // runs scenario
            this.v$.$touch();
            this.alertOnInvalidFields(this.v$, ev, false);
            if (!this.v$.$invalid) {
                this.loading = true;
                let message = "";
                //calls the action that runs the scenario and gets any error messages that were thrown
                message = await this.storeRunScenario({scenarioName: this.scenarioName});
                this.countDownChanged(0);
                if (message !== "No Error") {
                    // if errors were thrown display an error alert
                    this.showAlert("danger", "There was an error when running this scenario. " + message);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                } else {
                    // if there are previously saved scenarios check for changes in exception values or rules
                    await this.checkChangeInExceptions();
                    if (this.getDisplayValueChangeMessage) {
                        this.$bvModal.msgBoxOk(`WARNING: Change in value or rule requirements has occurred on an Existing Exception(s), you will need to reconfirm or the Exception(s) will be removed upon 'Save Scenario'.`);
                    }
                    if (this.getDisplayCompFactorMessage) {
                        this.$bvModal.msgBoxOk(`WARNING: Due to insufficient Compensating Factors, Exceptions will be removed upon clicking 'Save Scenario'.`);
                    }
                }
            }
            this.loading = false;
        },

        async saveScenario(ev) {
            // saves scenario
            this.v$.$touch();
            this.alertOnInvalidFields(this.v$, ev, false);
            if (!this.v$.$invalid) {
                let confirmation;
                // check if there is a rule with an override where the override is unconfirmed or if the passingCompFactorLength is less than 2 and a exception level is at least 2
                if (this.getScenario.rules.some(r => (r.override && (!r.override.confirmed || (this.getPassingCompFactorLength < 2 && r.exceptionLevel >= 2))))) {
                    // display alert
                    confirmation = await this.confirmReset('save');
                }
                // if the confirmation value was never set because there are no special cases or if the confirmation value was set to true by user
                if (confirmation === undefined || confirmation === true) {
                    // remove overrides and save scenario
                    this.loading = true;
                    // remove old non-applicable overrides
                    this.removeRuleOverrides();
                    let alertVals;
                    // calls the action that saves the scenario and get the message and variant for the status alert
                    alertVals = await this.storeSaveScenario();
                    this.countDownChanged(0);
                    this.loading = false;
                    this.showAlert(alertVals.variant, alertVals.message);
                    this.fetchScenarioTimes();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                }
            }
        },
    },
    computed: {
        ...mapGetters([
            "getHasRun", 
            "getSaved", 
            "getScenario", 
            "getScenarioByName", 
            "getCompareOptions", 
            "getDisplayValueChangeMessage", 
            "getDisplayCompFactorMessage", 
            "getRulesToCrse", 
            "getIsLegacyLoan", 
            "getPassingCompFactorLength",
            "userIsInBetaGroup"
        ]),
    },
    watch: {
        v$: {
            handler(validation) {
                this.setInvalidSections({ scenario: this.scenarioName });
                this.setInvalidSections({ scenario: this.scenarioName, errors: validation.$errors });
            },
        },
    },
    mounted() {
        console.log('print halo scenario mount')
        if (this.getScenario.id === null) {
            this.setSelectedLoanId(this.$route.params.id);
            this.fetchScenario({ scenario: this.scenarioName });
        } else {
            this.v$.$touch();
        }

        this.fetchScenarioTimes();
        // listener for updating encompass values
        document.addEventListener('updateEncompassValues', function() {
            this.fetchNewEncompassValues();
            }.bind(this));

        // listener for setting list of users
        document.addEventListener('grantedByUsers', function(e) {
            this.setUsers(e.detail)
            }.bind(this));
    },

    mixins: [ruleDialog, alertMixins, dialogMixins],
};
</script>
<style>
.alert-fade-enter-active {
    transition: all 1s ease;
    opacity: 0;
    transform: translateY(50px);
}

.alert-fade-enter-to {
    transition: all 1s ease;
    opacity: 1;
    transform: translateY(0);
}

.alert-fade-leave-to {
    transition: all 1s ease;
    opacity: 0;
}

.alert-fade-leave-active {
    transition: all 1s ease;
    transform: translateY(50px);
}
</style>

<style scoped>
.alert {
    width: 100%;
    margin-bottom: 1rem;
}
.ui-state-disabled:hover {
    cursor: default !important;
}

.b-group {
    overflow: hidden;
    margin-right: 5%
}

.run-button {
    background-color: #00afef;
    border-color: #00afef;
    overflow: hidden;
}

.save-button {
    background-color: #006891;
    border-color: #006891;
}

.save-button:disabled {
    opacity: 0.4;
    margin-top: 2px;
    background-color: slategrey;
    border-color: slategrey;
    overflow: hidden;
}

.save-button:focus,
.save-button:active {
    background-color: #346375 !important;
    border-color: #346375 !important;
}
</style>
